import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Tabs,
  Tab,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Autocomplete,
  LinearProgress,
  Snackbar
} from '@mui/material';
import {
  Assessment,
  Security,
  TrendingUp,
  Business,
  LocalHospital,
  Warning,
  Download,
  Person,
  CalendarToday,
  Description,
  CheckCircle,
  Error
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { auth } from '../firebase/config';
import reportsApiService from '../services/reports-api';
import apiService from '../services/api';

interface ReportCategory {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  color: string;
  reports: Report[];
}

interface Report {
  id: string;
  name: string;
  description: string;
  category: string;
  roleAccess: string[];
  parameters: ReportParameter[];
  lastGenerated?: string;
  status: 'available' | 'generating' | 'error';
}

interface ReportParameter {
  name: string;
  type: 'date' | 'dateRange' | 'select' | 'text' | 'number';
  label: string;
  required: boolean;
  options?: string[];
  defaultValue?: any;
}

interface ReportDialogProps {
  open: boolean;
  report: Report | null;
  onClose: () => void;
  onGenerate: (parameters: any) => void;
  generating: boolean;
  progress?: number;
}

interface Patient {
  id: string;
  name: string;
  mrn: string;
  dateOfBirth: string;
}

interface GeneratedReport {
  reportId: string;
  reportName: string;
  status: 'completed' | 'error';
  content?: any;
  downloadUrl?: string;
  generatedAt?: string;
  parameters: any;
  error?: string;
}

interface ReportDisplayDialogProps {
  open: boolean;
  report: GeneratedReport | null;
  onClose: () => void;
  onDownload: (reportId: string) => void;
}

// Icon mapping for dynamic icons
const iconMap = {
  Security: <Security />,
  TrendingUp: <TrendingUp />,
  Business: <Business />,
  LocalHospital: <LocalHospital />,
  Warning: <Warning />
};



const ReportDialog: React.FC<ReportDialogProps> = ({
  open,
  report,
  onClose,
  onGenerate,
  generating,
  progress = 0
}) => {
  const [parameters, setParameters] = useState<any>({});
  const [patients, setPatients] = useState<Patient[]>([]);
  const [loadingPatients, setLoadingPatients] = useState(false);

  useEffect(() => {
    if (report) {
      const initialParams: any = {};
      report.parameters.forEach(param => {
        if (param.defaultValue !== undefined) {
          initialParams[param.name] = param.defaultValue;
        }
      });
      setParameters(initialParams);
    }
  }, [report]);

  // Fetch patients for dropdown
  useEffect(() => {
    const fetchPatients = async () => {
      setLoadingPatients(true);
      try {
        console.log('Fetching patients for reports...');
        // Use the specific getPatients method instead of generic makeRequest
        const response = await apiService.getPatients(1, 100);
        console.log('Patients API response:', response);
        
        // Transform the API response to match the expected interface
        const transformedPatients = (response.patients || []).map((patient: any) => ({
          id: patient.id,
          name: patient.name,
          mrn: patient.mrn,
          dateOfBirth: patient.dob
        }));
        
        console.log('Transformed patients:', transformedPatients);
        setPatients(transformedPatients);
      } catch (error) {
        console.error('Error fetching patients:', error);
        // Fallback to mock patients if API fails
        setPatients([
          { id: '1', name: 'John Smith', mrn: 'MRN001', dateOfBirth: '1980-05-15' },
          { id: '2', name: 'Jane Doe', mrn: 'MRN002', dateOfBirth: '1975-12-03' },
          { id: '3', name: 'Robert Johnson', mrn: 'MRN003', dateOfBirth: '1990-08-22' },
          { id: '4', name: 'Mary Williams', mrn: 'MRN004', dateOfBirth: '1985-03-10' },
          { id: '5', name: 'David Brown', mrn: 'MRN005', dateOfBirth: '1972-11-28' },
        ]);
      } finally {
        setLoadingPatients(false);
      }
    };

    if (open) {
      fetchPatients();
    }
  }, [open]);

  const handleParameterChange = (name: string, value: any) => {
    setParameters((prev: any) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleGenerate = () => {
    console.log('Generate button clicked!');
    console.log('Parameters:', parameters);
    console.log('onGenerate function:', onGenerate);
    
    // Check if all required parameters are filled
    if (report) {
      const requiredParams = report.parameters.filter(p => p.required);
      const missingParams = requiredParams.filter(p => {
        // Check if the parameter exists in any form
        const hasParameter = parameters[p.name] || 
                           parameters[`${p.name}Start`] || 
                           parameters[`${p.name}End`];
        console.log(`Parameter ${p.name}:`, { 
          exists: !!hasParameter, 
          value: parameters[p.name],
          startValue: parameters[`${p.name}Start`],
          endValue: parameters[`${p.name}End`]
        });
        return !hasParameter;
      });
      console.log('Required parameters:', requiredParams);
      console.log('Missing parameters:', missingParams);
      
      if (missingParams.length > 0) {
        console.log('Missing required parameters:', missingParams.map(p => p.name));
        return;
      }
    }
    
    onGenerate(parameters);
  };

  const renderParameterInput = (param: ReportParameter) => {
    switch (param.type) {
      case 'date':
        return (
          <TextField
            key={param.name}
            label={param.label}
            type="date"
            value={parameters[param.name] || ''}
            onChange={(e) => handleParameterChange(param.name, e.target.value)}
            required={param.required}
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
        );
      case 'dateRange':
        return (
          <Box key={param.name} sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label={`${param.label} - Start`}
              type="date"
              value={parameters[`${param.name}Start`] || ''}
              onChange={(e) => handleParameterChange(`${param.name}Start`, e.target.value)}
              required={param.required}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label={`${param.label} - End`}
              type="date"
              value={parameters[`${param.name}End`] || ''}
              onChange={(e) => handleParameterChange(`${param.name}End`, e.target.value)}
              required={param.required}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Box>
        );
      case 'select':
        // Special handling for patient selection
        if (param.name === 'patientId') {
          return (
            <Autocomplete
              key={param.name}
              options={patients}
              loading={loadingPatients}
              getOptionLabel={(option) => `${option.name}${option.mrn ? ` (${option.mrn})` : ''}`}
              value={patients.find(p => p.id === parameters[param.name]) || null}
              onChange={(event, newValue) => {
                handleParameterChange(param.name, newValue?.id || '');
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={param.label}
                  required={param.required}
                  placeholder="Search patients..."
                />
              )}
              renderOption={(props, option) => (
                <Box component="li" {...props}>
                  <Box>
                    <Typography variant="body1">{option.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {option.mrn ? `MRN: ${option.mrn} | ` : ''}DOB: {new Date(option.dateOfBirth).toLocaleDateString()}
                    </Typography>
                  </Box>
                </Box>
              )}
              fullWidth
            />
          );
        }
        
        return (
          <FormControl key={param.name} fullWidth required={param.required}>
            <InputLabel>{param.label}</InputLabel>
            <Select
              value={parameters[param.name] || ''}
              onChange={(e) => handleParameterChange(param.name, e.target.value)}
              label={param.label}
            >
              {param.options?.map(option => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );
      case 'text':
        return (
          <TextField
            key={param.name}
            label={param.label}
            value={parameters[param.name] || ''}
            onChange={(e) => handleParameterChange(param.name, e.target.value)}
            required={param.required}
            fullWidth
          />
        );
      case 'number':
        return (
          <TextField
            key={param.name}
            label={param.label}
            type="number"
            value={parameters[param.name] || ''}
            onChange={(e) => handleParameterChange(param.name, e.target.value)}
            required={param.required}
            fullWidth
          />
        );
      default:
        return null;
    }
  };

  if (!report) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Assessment />
          Generate Report: {report.name}
        </Box>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {report.description}
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {report.parameters.map(renderParameterInput)}
        </Box>
        {generating && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Generating report... {progress > 0 && `${progress}%`}
            </Typography>
            <LinearProgress 
              variant={progress > 0 ? "determinate" : "indeterminate"} 
              value={progress}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={generating}>
          Cancel
        </Button>
        <Button
          onClick={handleGenerate}
          variant="contained"
          disabled={generating}
          startIcon={generating ? <CircularProgress size={20} /> : <Download />}
        >
          {generating ? 'Generating...' : 'Generate Report'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const ReportDisplayDialog: React.FC<ReportDisplayDialogProps> = ({
  open,
  report,
  onClose,
  onDownload
}) => {
  if (!report) return null;

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  const renderReportContent = () => {
    if (report.status === 'error') {
      return (
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography variant="body1">
            Error generating report: {report.error || 'Unknown error'}
          </Typography>
        </Alert>
      );
    }

    // Get patient name from ID
    const patientId = report.parameters.patientId;
    const patientName = patientId ? `Patient ${patientId}` : 'All Patients';

    // Mock report content based on report type
    const reportType = report.reportName.toLowerCase();
    
    return (
      <Box>
        <Typography variant="h6" gutterBottom>
          Report Summary
        </Typography>
        
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          <strong>Patient:</strong> {patientName}
        </Typography>
        
        {reportType.includes('surgical') && (
          <Box>
            <Typography variant="body1" paragraph>
              <strong>Gross Description:</strong> Large excision specimen measuring 8.5 x 6.2 x 3.1 cm. 
              The specimen is oriented and inked. Multiple sections are submitted for microscopic examination.
            </Typography>
            <Typography variant="body1" paragraph>
              <strong>Microscopic Description:</strong> Sections show well-differentiated adenocarcinoma 
              with glandular formation. Tumor cells demonstrate moderate nuclear atypia. No lymphovascular 
              invasion identified. Margins appear free of tumor.
            </Typography>
            <Typography variant="body1">
              <strong>Diagnosis:</strong> Well-differentiated adenocarcinoma, grade 2/3
            </Typography>
          </Box>
        )}
        
        {reportType.includes('biopsy') && (
          <Box>
            <Typography variant="body1" paragraph>
              <strong>Procedure:</strong> Core needle biopsy
            </Typography>
            <Typography variant="body1" paragraph>
              <strong>Microscopic Description:</strong> Sections show fragments of tissue with 
              infiltrating carcinoma. Tumor cells demonstrate moderate nuclear atypia and mitotic activity.
            </Typography>
            <Typography variant="body1">
              <strong>Diagnosis:</strong> Invasive ductal carcinoma, grade 2/3
            </Typography>
          </Box>
        )}
        
        {reportType.includes('fna') && (
          <Box>
            <Typography variant="body1" paragraph>
              <strong>Procedure:</strong> Fine-needle aspiration cytology
            </Typography>
            <Typography variant="body1" paragraph>
              <strong>Cytologic Description:</strong> Smears show cellular material with clusters 
              of epithelial cells. Nuclear features are consistent with benign/reactive changes.
            </Typography>
            <Typography variant="body1">
              <strong>Diagnosis:</strong> Benign epithelial cells, no malignancy identified
            </Typography>
          </Box>
        )}
        
        {reportType.includes('frozen') && (
          <Box>
            <Typography variant="body1" paragraph>
              <strong>Procedure:</strong> Frozen section examination
            </Typography>
            <Typography variant="body1" paragraph>
              <strong>Intraoperative Diagnosis:</strong> Sections show benign breast tissue with 
              no evidence of malignancy. Fibrocystic changes present.
            </Typography>
            <Typography variant="body1">
              <strong>Note:</strong> Final diagnosis pending permanent sections
            </Typography>
          </Box>
        )}
        
        {reportType.includes('ihc') && (
          <Box>
            <Typography variant="body1" paragraph>
              <strong>Immunohistochemistry Panel Results:</strong>
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
                <ListItemText primary="ER: Positive (80%)" />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
                <ListItemText primary="PR: Positive (60%)" />
              </ListItem>
              <ListItem>
                <ListItemIcon><Error color="error" /></ListItemIcon>
                <ListItemText primary="HER2: Negative (0/3+)" />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
                <ListItemText primary="Ki-67: 15% (low proliferation)" />
              </ListItem>
            </List>
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Assessment />
          {report.reportName}
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Report Details
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Person fontSize="small" />
                <Typography variant="body2">
                  <strong>Patient:</strong> {report.parameters.patientId || 'All Patients'}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CalendarToday fontSize="small" />
                <Typography variant="body2">
                  <strong>Generated:</strong> {formatDate(report.generatedAt)}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>
        
        <Divider sx={{ my: 2 }} />
        
        {renderReportContent()}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>
          Close
        </Button>
        {report.status === 'completed' && report.downloadUrl && (
          <Button
            onClick={() => onDownload(report.reportId)}
            variant="contained"
            startIcon={<Download />}
          >
            Download PDF
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

const Reports: React.FC = () => {
  const { userProfile } = useAuth();
  const [selectedCategory, setSelectedCategory] = useState(0);
  const [selectedReport, setSelectedReport] = useState<Report | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [reports, setReports] = useState<ReportCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [generatedReport, setGeneratedReport] = useState<GeneratedReport | null>(null);
  const [displayDialogOpen, setDisplayDialogOpen] = useState(false);
  const [progress, setProgress] = useState(0);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const maxRetries = 3;
  const [showHistory, setShowHistory] = useState(false);
  const [reportHistory, setReportHistory] = useState<any[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    const fetchReports = async () => {
      try {
        console.log('Fetching reports from API...');
        
        // Debug authentication state
        const user = auth.currentUser;
        console.log('Current user:', user ? user.uid : 'No user');
        console.log('User profile from context:', userProfile);
        
        if (user) {
          try {
            const token = await user.getIdToken();
            console.log('Token available:', !!token);
            console.log('Token length:', token ? token.length : 0);
          } catch (tokenError) {
            console.error('Error getting token:', tokenError);
          }
        }
        
        const categoriesResponse = await reportsApiService.getCategories();
        console.log('Categories API response:', categoriesResponse);
        
        // Fetch all reports separately
        const reportsResponse = await reportsApiService.getReports();
        console.log('Reports API response:', reportsResponse);
        
        // Transform API response to match our interface
        const transformedReports = categoriesResponse.map((category: any) => {
          // Find reports that belong to this category
          const categoryReports = reportsResponse.filter((report: any) => 
            report.categoryId === category.id
          ).map((report: any) => ({
            id: report.id,
            name: report.name,
            description: report.description,
            category: category.title,
            roleAccess: JSON.parse(report.roleAccess || '[]'),
            parameters: JSON.parse(report.parameters || '[]'),
            lastGenerated: report.lastGenerated,
            status: report.status
          }));
          
          return {
            id: category.id,
            title: category.title,
            description: category.description,
            icon: iconMap[category.icon as keyof typeof iconMap] || <Assessment />,
            color: category.color,
            reports: categoryReports
          };
        });
        
        // Remove categories that have no reports to prevent empty sections
        const filteredReports = transformedReports.filter(category => category.reports.length > 0);
        
        setReports(filteredReports);
      } catch (error) {
        console.error('Error fetching reports:', error);
        console.log('Falling back to mock data...');
        // Fallback to mock data for development
        setReports([]);
      } finally {
        setLoading(false);
      }
    };
    
    fetchReports();
  }, [userProfile]);

  const handleCategoryChange = (event: React.SyntheticEvent, newValue: number) => {
    setSelectedCategory(newValue);
  };

  const handleReportClick = (report: Report) => {
    setSelectedReport(report);
    setDialogOpen(true);
  };

  const handleGenerateReport = async (parameters: any) => {
    console.log('handleGenerateReport called with parameters:', parameters);
    setGenerating(true);
    setProgress(0);
    setErrorMessage(null);
    
    try {
      // Debug authentication state
      const { auth } = await import('../firebase/config');
      console.log('Current user:', auth.currentUser);
      if (auth.currentUser) {
        const token = await auth.currentUser.getIdToken();
        console.log('Auth token available:', !!token);
        console.log('Token preview:', token ? token.substring(0, 50) + '...' : 'No token');
      } else {
        console.log('No authenticated user found');
      }
      
      // Fix parameter formatting for API calls
      const formattedParameters = { ...parameters };
      
      // Handle dateRange parameters - combine start and end dates
      Object.keys(parameters).forEach(key => {
        if (key.endsWith('Start') || key.endsWith('End')) {
          const baseKey = key.replace('Start', '').replace('End', '');
          if (!formattedParameters[baseKey]) {
            formattedParameters[baseKey] = {};
          }
          if (key.endsWith('Start')) {
            formattedParameters[baseKey].start = parameters[key];
          } else {
            formattedParameters[baseKey].end = parameters[key];
          }
          delete formattedParameters[key];
        }
      });
      
      console.log('Formatted parameters for API:', formattedParameters);
      console.log('Selected report ID:', selectedReport?.id);
      console.log('Selected report type:', typeof selectedReport?.id);
      
             // Call the real API
       const response = await reportsApiService.generateReport({
         reportId: selectedReport?.id!,
         parameters: formattedParameters,
         format: 'pdf'
       });
       
       console.log('Report generation response:', response);
       
       // The backend returns {id: 'uuid'} but we need to use the original reportId for polling
       const generatedReportId = response.id || response.reportId;
       if (generatedReportId) {
         console.log('Starting to poll for report status with ID:', generatedReportId);
         // Poll for status until complete
         const status = await reportsApiService.pollReportStatus(
           generatedReportId,
           (progressValue) => {
             console.log('Report generation progress:', progressValue);
             setProgress(progressValue || 0);
           }
         );
         
         console.log('Polling completed, status:', status);
        
        if (status.status === 'completed') {
          // Show report in dialog
          setGeneratedReport({
            reportId: generatedReportId,
            reportName: selectedReport?.name || 'Unknown Report',
            status: 'completed',
            parameters: formattedParameters,
            generatedAt: status.generatedAt,
            downloadUrl: status.downloadUrl
          });
          setDisplayDialogOpen(true);
          setDialogOpen(false);
          setSelectedReport(null);
          setProgress(100);
          setSuccessMessage(`Report "${selectedReport?.name}" generated successfully!`);
          
          // Refresh reports to update lastGenerated timestamps
          const fetchReports = async () => {
            try {
              const categoriesResponse = await reportsApiService.getCategories();
              const reportsResponse = await reportsApiService.getReports();
              
              // Transform API response to match our interface
              const transformedReports = categoriesResponse.map((category: any) => {
                // Find reports that belong to this category
                const categoryReports = reportsResponse.filter((report: any) => 
                  report.categoryId === category.id
                ).map((report: any) => ({
                  id: report.id,
                  name: report.name,
                  description: report.description,
                  category: category.title,
                  roleAccess: JSON.parse(report.roleAccess || '[]'),
                  parameters: JSON.parse(report.parameters || '[]'),
                  lastGenerated: report.lastGenerated,
                  status: report.status
                }));
                
                return {
                  id: category.id,
                  title: category.title,
                  description: category.description,
                  icon: iconMap[category.icon as keyof typeof iconMap] || <Assessment />,
                  color: category.color,
                  reports: categoryReports
                };
              });
              
              // Remove categories that have no reports to prevent empty sections
              const filteredReports = transformedReports.filter(category => category.reports.length > 0);
              
              setReports(filteredReports);
            } catch (error) {
              console.error('Error refreshing reports:', error);
            }
          };
          fetchReports();
        } else {
          throw 'Report generation failed';
        }
      }
      
    } catch (error) {
      console.error('Error generating report:', error);
      
      // Enhanced error handling with retry logic
      const errorMessage = error && typeof error === 'object' && 'message' in error 
        ? String((error as any).message) 
        : String(error);
      
      if (retryCount < maxRetries && errorMessage.includes('timeout')) {
        setRetryCount(prev => prev + 1);
        setErrorMessage(`Generation timed out. Retrying... (${retryCount + 1}/${maxRetries})`);
        
        // Retry after 3 seconds
        setTimeout(() => {
          handleGenerateReport(parameters);
        }, 3000);
        return;
      }
      
      // Show error in display dialog
      setGeneratedReport({
        reportId: `error-${Date.now()}`,
        reportName: selectedReport?.name || 'Unknown Report',
        status: 'error',
        parameters: parameters,
        error: errorMessage
      });
      setDisplayDialogOpen(true);
      setDialogOpen(false);
      setSelectedReport(null);
      setErrorMessage(errorMessage);
    } finally {
      setGenerating(false);
      setRetryCount(0);
    }
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedReport(null);
  };

  const handleCloseDisplayDialog = () => {
    setDisplayDialogOpen(false);
    setGeneratedReport(null);
  };

  const handleDownloadReport = (reportId: string) => {
    // In real implementation, this would trigger the actual download
    console.log('Downloading report:', reportId);
    // For now, just show a success message
    alert('Download started! (Mock implementation)');
  };

  const fetchReportHistory = async () => {
    setLoadingHistory(true);
    try {
      const response = await reportsApiService.getReportHistory();
      setReportHistory(response.history || []);
    } catch (error) {
      console.error('Error fetching report history:', error);
      setReportHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleToggleHistory = () => {
    setShowHistory(!showHistory);
    if (!showHistory && reportHistory.length === 0) {
      fetchReportHistory();
    }
  };



  const filteredReports = useMemo(() => {
    // Show all reports without any role-based filtering
    return reports[selectedCategory]?.reports || [];
  }, [reports, selectedCategory]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (reports.length === 0) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">
          No reports available. Please run the setup script to populate the database.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Reports & Analytics
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Generate comprehensive reports for regulatory compliance, quality management, and operational insights.
          </Typography>
        </Box>
        <Button
          variant={showHistory ? "contained" : "outlined"}
          onClick={handleToggleHistory}
          startIcon={<Description />}
        >
          {showHistory ? "Show Reports" : "View History"}
        </Button>
      </Box>

      

      {!showHistory ? (
        <Paper sx={{ mb: 3 }}>
                     <Tabs
             value={selectedCategory}
             onChange={handleCategoryChange}
             variant="scrollable"
             scrollButtons={true}
             sx={{ borderBottom: 1, borderColor: 'divider' }}
           >
            {reports.map((category, index) => (
              <Tab
                key={category.id}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ color: category.color }}>{category.icon}</Box>
                    {category.title}
                  </Box>
                }
                value={index}
              />
            ))}
          </Tabs>
        </Paper>
      ) : (
        <Paper sx={{ mb: 3, p: 2 }}>
          <Typography variant="h6" gutterBottom>
            Report History
          </Typography>
          <Typography variant="body2" color="text.secondary">
            View your previously generated reports and download them again if needed.
          </Typography>
        </Paper>
      )}

              {!showHistory ? (
          reports[selectedCategory] && (
            <Box>
              <Typography variant="h6" gutterBottom>
                {reports[selectedCategory].title}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {reports[selectedCategory].description}
              </Typography>

              <Grid container spacing={3}>
                {filteredReports.map((report) => (
                  <Grid item xs={12} sm={6} md={4} key={report.id}>
                    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                      <CardContent sx={{ flexGrow: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                          <Assessment sx={{ color: reports[selectedCategory].color }} />
                          <Typography variant="h6" component="div">
                            {report.name}
                          </Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          {report.description}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          {report.roleAccess.map(role => (
                            <Chip
                              key={role}
                              label={role}
                              size="small"
                              variant="outlined"
                              sx={{ fontSize: '0.75rem' }}
                            />
                          ))}
                        </Box>
                      </CardContent>
                      <CardActions>
                        <Button
                          size="small"
                          startIcon={<Download />}
                          onClick={() => handleReportClick(report)}
                          disabled={report.status !== 'available'}
                        >
                          Generate
                        </Button>
                        {report.lastGenerated && (
                          <Typography variant="caption" color="text.secondary">
                            Last: {new Date(report.lastGenerated).toLocaleDateString()}
                          </Typography>
                        )}
                      </CardActions>
                    </Card>
                  </Grid>
                ))}
              </Grid>

                             {filteredReports.length === 0 && (
                 <Alert severity="info">
                   No reports available in this category.
                 </Alert>
               )}
            </Box>
          )
        ) : (
          <Box>
            {loadingHistory ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
                <CircularProgress />
              </Box>
            ) : (
              <Grid container spacing={3}>
                {reportHistory.map((historyItem) => (
                  <Grid item xs={12} sm={6} md={4} key={historyItem.id}>
                    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                      <CardContent sx={{ flexGrow: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                          <Assessment />
                          <Typography variant="h6" component="div">
                            {historyItem.reportName}
                          </Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          Category: {historyItem.category}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <CalendarToday fontSize="small" />
                          <Typography variant="caption" color="text.secondary">
                            Generated: {new Date(historyItem.generatedAt).toLocaleString()}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip
                            label={historyItem.status}
                            size="small"
                            color={historyItem.status === 'completed' ? 'success' : 'error'}
                            variant="outlined"
                          />
                        </Box>
                      </CardContent>
                      <CardActions>
                        {historyItem.status === 'completed' && historyItem.downloadUrl && (
                          <Button
                            size="small"
                            startIcon={<Download />}
                            onClick={() => handleDownloadReport(historyItem.reportId)}
                          >
                            Download
                          </Button>
                        )}
                        <Button
                          size="small"
                          onClick={() => {
                            setGeneratedReport({
                              reportId: historyItem.reportId,
                              reportName: historyItem.reportName,
                              status: historyItem.status as 'completed' | 'error',
                              parameters: historyItem.parameters ? JSON.parse(historyItem.parameters) : {},
                              generatedAt: historyItem.generatedAt,
                              downloadUrl: historyItem.downloadUrl,
                              error: historyItem.status === 'error' ? 'Report generation failed' : undefined
                            });
                            setDisplayDialogOpen(true);
                          }}
                        >
                          View
                        </Button>
                      </CardActions>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}

            {!loadingHistory && reportHistory.length === 0 && (
              <Alert severity="info">
                No report history found. Generate your first report to see it here.
              </Alert>
            )}
          </Box>
        )}

      <ReportDialog
        open={dialogOpen}
        report={selectedReport}
        onClose={handleCloseDialog}
        onGenerate={handleGenerateReport}
        generating={generating}
        progress={progress}
      />

      <ReportDisplayDialog
        open={displayDialogOpen}
        report={generatedReport}
        onClose={handleCloseDisplayDialog}
        onDownload={handleDownloadReport}
      />
      
      {/* Error Notification Snackbar */}
      <Snackbar
        open={!!errorMessage}
        autoHideDuration={6000}
        onClose={() => setErrorMessage(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setErrorMessage(null)} 
          severity="error" 
          sx={{ width: '100%' }}
        >
          {errorMessage}
        </Alert>
      </Snackbar>

      {/* Success Notification Snackbar */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={4000}
        onClose={() => setSuccessMessage(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSuccessMessage(null)} 
          severity="success" 
          sx={{ width: '100%' }}
        >
          {successMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Reports; 